import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Seo from '../components/Seo';
import { portfolioSchema } from '../components/structuredData';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Button, 
  Chip, 
  IconButton,
  Avatar,
  useTheme,
  useMediaQuery,
  Fade,
  AppBar,
  Toolbar,
  Slide,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { 
  GitHub, 
  LinkedIn, 
  Launch, 
  KeyboardArrowDown,
  Person,
  Work
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';

// Helper function to handle both local and remote images
const getImageUrl = (imagePath: string, type: 'projects' | 'publications' = 'projects'): string => {
  // If it's already a full URL (http/https), return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // If it starts with '/', treat as absolute path from public folder
  if (imagePath.startsWith('/')) {
    return imagePath;
  }
  
  // Otherwise, treat as relative path from public/images/{type}/
  return `/images/${type}/${imagePath}`;
};

// Animations
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;



// Styled Components
const HeroSection = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: `linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.1)',
    zIndex: 1,
  }
}));

const FloatingElement = styled(Box)(({ theme }) => ({
  animation: `${float} 6s ease-in-out infinite`,
  zIndex: 2,
  position: 'relative',
}));




const SkillChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  borderRadius: '8px',
  backgroundColor: '#404040',
  color: '#e0e0e0',
  fontWeight: '500',
  border: '1px solid #505050',
  '&:hover': {
    backgroundColor: '#505050',
    color: '#f0f0f0',
    transform: 'translateY(-1px)',
    transition: 'all 0.2s ease',
  }
}));

const scrollBounce = keyframes`
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(8px); }
`;

const ScrollButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  bottom: '2rem',
  left: '50%',
  transform: 'translateX(-50%)',
  color: 'white',
  animation: `${scrollBounce} 2s ease-in-out infinite`,
  zIndex: 2,
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  }
}));

const FeaturedProjectCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  borderRadius: '16px',
  overflow: 'hidden',
  position: 'relative',
  background: '#2a2a2a',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  '&:hover': {
    transform: 'translateY(-12px)',
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.4)',
  },
  '&:hover .featured-overlay': {
    opacity: 1,
  },
  '&:hover .project-image': {
    transform: 'scale(1.05)',
  }
}));


const ProjectImage = styled(CardMedia)(({ theme }) => ({
  transition: 'transform 0.4s ease',
  height: '300px',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
}));

const FeaturedOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'linear-gradient(135deg, rgba(20, 20, 20, 0.97) 0%, rgba(30, 30, 30, 0.95) 100%)',
  backdropFilter: 'blur(15px)',
  display: 'flex',
  flexDirection: 'column',
  opacity: 0,
  transition: 'opacity 0.4s ease',
  color: 'white',
  padding: theme.spacing(4),
  justifyContent: 'space-between',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#f0f0f0',
  fontWeight: '600',
  marginBottom: theme.spacing(4),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-10px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '60px',
    height: '3px',
    background: '#666666',
    borderRadius: '2px',
  }
}));

const NavigationBar = styled(AppBar)(({ theme }) => ({
  background: 'rgba(26, 26, 26, 0.95)',
  backdropFilter: 'blur(20px)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
}));

const NavButton = styled(Button)(({ theme }) => ({
  color: '#e0e0e0',
  textTransform: 'none',
  fontWeight: '500',
  padding: '8px 16px',
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    transform: 'translateY(-1px)',
  }
}));

const PublicationCard = styled(Card)(({ theme }) => ({
  // A4 aspect ratio: 1:1.414 (width:height)
  aspectRatio: '1 / 1.414',
  maxWidth: '300px',
  margin: '0 auto',
  background: '#2a2a2a',
  borderRadius: '8px',
  overflow: 'hidden',
  position: 'relative',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 16px 32px rgba(0, 0, 0, 0.4)',
  },
  '&:hover .publication-overlay': {
    opacity: 1,
  }
}));

const PublicationOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  background: 'linear-gradient(to top, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.8) 50%, transparent 100%)',
  padding: theme.spacing(3),
  color: 'white',
  opacity: 0,
  transition: 'opacity 0.3s ease',
}));

// Portfolio data (you can replace these with your actual data)
const portfolioData = {
  name: "Vemund Fredriksen",
  title: ".NET Developer",
  bio: "Professional .NET Developer with a masters degree in artificial intelligence.",
  avatar: "https://avatars.githubusercontent.com/u/22099846?v=4",
  
  projects: [
    {
      id: 1,
      title: "Lung Tumor Mask",
      description: "A CLI tool for segmenting lung tumors from CT scans using a pretrained neural network.",
      longDescription: "This project was the result of my master's thesis in artificial intelligence. It utilizes a pretrained neural network to accurately segment lung tumors from CT scans. The repository has gotten significant attention throughout the years.",
      image: getImageUrl("lungtumormask.png"),
      technologies: ["Neural Networks", "Deep Learning", "Python", "Pytorch", "Monai"],
      github: "https://github.com/VemundFredriksen/LungTumorMask",
      demo: "https://huggingface.co/spaces/andreped/LungTumorMask",
      featured: true
    },
    {
      id: 2,
      title: "Underlag.no",
      description: "A service for runners located in Oslo to check and report on running conditions of popular routes in the city.",
      longDescription: "During the winter of 2025, I frequently reached out to a fellow runner in the community to check the conditions of a popular interval course in Oslo, as they varied greatly with the weather. This sparked the idea of creating a dedicated website to share such updates. Within just a few days of development, we had a functional proof of concept running at www.underlag.no",
      image: "underlag.png",
      technologies: ["ASP.NET Core", "Entity Framework", "MediatR", "React", "PostgreSQL", "Docker compose", ],
      demo: "https://underlag.no",
      featured: true
    },
    {
      id: 3,
      title: "Synthlung",
      description: "Project to create a synthetic dataset for lung CT scans with tumors to accomodate the need for more training data in the medical imaging field.",
      longDescription: "This project was put on ice a while ago. I would love for someone to pick up where we left off, as I believe this could be a really interesting project to work on. The goal was to create a synthetic dataset of lung CT scans with tumors, to help address the shortage of training data in the medical imaging field. The readme in the repository has an overview of the next steps that should be implemented",
      image: getImageUrl("synthlung.png"),
      technologies: ["Python", "Monai"],
      github: "https://github.com/VemundFredriksen/SynthLung",
    //   demo: "https://project3-demo.com",
      featured: true
    },
    {
      id: 4,
      title: "ExpressiveFit",
      description: "A C# library for reading .FIT files popularily used by Garmin devices.",
      longDescription: "The library offers a wide range of expressive models that can be used to analyze activity data from the .FIT-file.",
      image: getImageUrl("expressive_fit.png"),
      technologies: ["C#"],
      github: "https://github.com/VemundFredriksen/ExpressiveFit",
    //   demo: "https://project3-demo.com",
      featured: true
    },
    {
      id: 5,
      title: "Shootr.gg",
      description: "A startup developing a platform for organizing football shooting competitions where participants compete by shooting on a PVC billboard.", 
      longDescription: "The startup is currently developing a platform for organizing competitions where participants can compete in shooting on PVC billboards. The platform allows users to register, create and join competitions, and track their progress. The project is still in the early stages, but the first competations have already been organized and paying customers are already using the platform.",
      image: getImageUrl("shootr.png"),
      technologies: ["Startup"],
    //   github: "https://github.com/yourusername/project3",
      demo: "https://www.shootr.gg",
      featured: true
    },
    {
      id: 6,
      title: "Beredskapsnabo.no",
      description: "Collaboration tool for one or more households to coordinate a joint emergency inventory.",
      longDescription: "My colleague invited me to create a platform for norwegian households to coordinate a joint emergency inventory. The idea is that households in a neighborhood can share resources and coordinate their emergency preparedness efforts. The project is still in the early stages, but we have a working prototype and are looking for feedback from potential users.",
      image: getImageUrl("beredskapsnabo.png"),
      technologies: [".NET", "React", "PostgreSQL", "Docker compose"],
    //   github: "",
      demo: "https://test.beredskapsnabo.no",
      featured: true
    }
  ]
};

const PortfolioPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showNavbar, setShowNavbar] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.getElementById('hero');
      if (heroSection) {
        const heroRect = heroSection.getBoundingClientRect();
        setShowNavbar(heroRect.bottom < 100);
      }

      const sections = ['hero', 'projects', 'publications'];
      sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      // Section visibility tracking removed as not used
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box>
      <Seo
        title={`${portfolioData.name} — ${portfolioData.title}`}
        description={`${portfolioData.bio} Portfolio of projects, publications and open source work in .NET, machine learning and medical imaging.`}
        path="/"
        structuredData={portfolioSchema}
      />
      {/* Navigation Bar */}
      <Slide direction="down" in={showNavbar} mountOnEnter unmountOnExit>
        <NavigationBar position="fixed" elevation={0}>
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: '600' }}>
              {portfolioData.name}
            </Typography>
            {isMobile ? (
              <>
                <IconButton
                  edge="end"
                  color="inherit"
                  aria-label="menu"
                  onClick={() => setDrawerOpen(true)}
                  sx={{ color: '#e0e0e0' }}
                >
                  <MenuIcon />
                </IconButton>
                <Drawer
                  anchor="right"
                  open={drawerOpen}
                  onClose={() => setDrawerOpen(false)}
                  PaperProps={{ sx: { background: 'linear-gradient(135deg, #232526 0%, #1a1a1a 100%)', minWidth: 270, boxShadow: '0 0 40px 10px #000a' } }}
                >
                  <Box sx={{ width: 270, pt: 0, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
                    {/* Creative Drawer Header */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      px: 2,
                      py: 3,
                      background: 'rgba(34, 40, 49, 0.95)',
                      borderBottom: '1px solid rgba(255,255,255,0.07)',
                      position: 'relative',
                      zIndex: 2
                    }}>
                      <Avatar src={portfolioData.avatar} sx={{ width: 48, height: 48, border: '2px solid #66bb6a', boxShadow: '0 2px 12px #66bb6a44' }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700, letterSpacing: 0.5 }}>{portfolioData.name}</Typography>
                        <Typography variant="caption" sx={{ color: '#66bb6a', fontWeight: 500 }}>{portfolioData.title}</Typography>
                      </Box>
                      <Box sx={{ flexGrow: 1 }} />
                      <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#e0e0e0' }}>
                        <CloseIcon />
                      </IconButton>
                    </Box>
                    {/* Subtle background effect */}
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      zIndex: 0,
                      background: 'radial-gradient(circle at 80% 10%, #66bb6a22 0%, transparent 60%)',
                      pointerEvents: 'none',
                    }} />
                    {/* Animated Menu List */}
                    <List sx={{ mt: 2, zIndex: 2, position: 'relative' }}>
                      {/* Internal navigation */}
                      <Fade in={drawerOpen} style={{ transitionDelay: drawerOpen ? '100ms' : '0ms' }}>
                        <ListItem disablePadding>
                          <ListItemButton onClick={() => { setDrawerOpen(false); setTimeout(() => scrollToSection('projects'), 200); }} sx={{ borderRadius: 2, mx: 1, mb: 1, transition: 'all 0.2s', '&:hover': { background: 'rgba(102,187,106,0.08)' } }}>
                            <ListItemIcon><Work sx={{ color: '#e0e0e0' }} /></ListItemIcon>
                            <ListItemText primary={<span style={{ color: '#e0e0e0', fontWeight: 500 }}>Projects</span>} />
                          </ListItemButton>
                        </ListItem>
                      </Fade>
                      <Fade in={drawerOpen} style={{ transitionDelay: drawerOpen ? '200ms' : '0ms' }}>
                        <ListItem disablePadding>
                          <ListItemButton component={RouterLink} to="/cv" onClick={() => setDrawerOpen(false)} sx={{ borderRadius: 2, mx: 1, mb: 1, transition: 'all 0.2s', '&:hover': { background: 'rgba(255,255,255,0.06)' } }}>
                            <ListItemIcon><Person sx={{ color: '#e0e0e0' }} /></ListItemIcon>
                            <ListItemText primary={<span style={{ color: '#e0e0e0', fontWeight: 500 }}>CV & Skills</span>} />
                          </ListItemButton>
                        </ListItem>
                      </Fade>

                      {/* Divider and label for external links */}
                      <Box sx={{ px: 2, py: 1, mt: 1, mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#b0b0b0', fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase' }}>
                          External Links
                        </Typography>
                      </Box>
                      <Fade in={drawerOpen} style={{ transitionDelay: drawerOpen ? '400ms' : '0ms' }}>
                        <ListItem disablePadding>
                          <ListItemButton onClick={() => { setDrawerOpen(false); setTimeout(() => window.open('https://github.com/VemundFredriksen', '_blank'), 200); }} sx={{ borderRadius: 2, mx: 1, mb: 1, transition: 'all 0.2s', '&:hover': { background: 'rgba(33,150,243,0.08)' } }}>
                            <ListItemIcon><GitHub sx={{ color: '#e0e0e0' }} /></ListItemIcon>
                            <ListItemText primary={<span style={{ color: '#e0e0e0', fontWeight: 500 }}>GitHub</span>} />
                            <OpenInNewIcon fontSize="small" sx={{ color: '#b0b0b0', ml: 1 }} />
                          </ListItemButton>
                        </ListItem>
                      </Fade>
                      <Fade in={drawerOpen} style={{ transitionDelay: drawerOpen ? '500ms' : '0ms' }}>
                        <ListItem disablePadding>
                          <ListItemButton onClick={() => { setDrawerOpen(false); setTimeout(() => window.open('https://www.linkedin.com/in/vemundfredriksen/', '_blank'), 200); }} sx={{ borderRadius: 2, mx: 1, mb: 1, transition: 'all 0.2s', '&:hover': { background: 'rgba(33,150,243,0.08)' } }}>
                            <ListItemIcon><LinkedIn sx={{ color: '#e0e0e0' }} /></ListItemIcon>
                            <ListItemText primary={<span style={{ color: '#e0e0e0', fontWeight: 500 }}>LinkedIn</span>} />
                            <OpenInNewIcon fontSize="small" sx={{ color: '#b0b0b0', ml: 1 }} />
                          </ListItemButton>
                        </ListItem>
                      </Fade>
                    </List>
                    {/* Optional: Add a creative footer or social icons here */}
                  </Box>
                </Drawer>
              </>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <NavButton 
                  startIcon={<Work />}
                  onClick={() => scrollToSection('projects')}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)'
                    }
                  }}
                >
                  Projects
                </NavButton>
                <NavButton
                  startIcon={<Person />}
                  component={RouterLink}
                  to="/cv"
                >
                  CV & Skills
                </NavButton>

                <IconButton 
                  onClick={() => window.open('https://github.com/VemundFredriksen', '_blank')}
                  sx={{ 
                    color: '#e0e0e0',
                    '&:hover': { color: 'white' }
                  }}
                >
                  <GitHub />
                </IconButton>
                <IconButton 
                  onClick={() => window.open('https://www.linkedin.com/in/vemundfredriksen/', '_blank')}
                  sx={{ 
                    color: '#e0e0e0',
                    '&:hover': { color: 'white' }
                  }}
                >
                  <LinkedIn />
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </NavigationBar>
      </Slide>

      {/* Hero Section */}
      <HeroSection id="hero">
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center" justifyContent="center">
            <Grid item xs={12} md={6} textAlign="center">
              <FloatingElement>
                <Avatar
                  src={portfolioData.avatar}
                  sx={{ 
                    width: { xs: 120, md: 160 }, 
                    height: { xs: 120, md: 160 }, 
                    margin: '0 auto 2rem',
                    border: '4px solid rgba(255, 255, 255, 0.3)',
                    backdropFilter: 'blur(10px)'
                  }}
                />
                <Typography 
                  variant={isMobile ? "h3" : "h2"} 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 'bold', 
                    mb: 1,
                    textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                  }}
                >
                  {portfolioData.name}
                </Typography>
                <Typography 
                  variant={isMobile ? "h5" : "h4"} 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.9)', 
                    mb: 3,
                    textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                  }}
                >
                  {portfolioData.title}
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.8)', 
                    mb: 4,
                    maxWidth: '500px',
                    margin: '0 auto 2rem'
                  }}
                >
                  {portfolioData.bio}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
                  <IconButton 
                    onClick={() => window.open('https://github.com/VemundFredriksen', '_blank')}
                    sx={{ 
                      color: 'white', 
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' }
                    }}
                  >
                    <GitHub />
                  </IconButton>
                  <IconButton 
                    onClick={() => window.open('https://www.linkedin.com/in/vemundfredriksen/', '_blank')}
                    sx={{ 
                      color: 'white', 
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' }
                    }}
                  >
                    <LinkedIn />
                  </IconButton>
                </Box>
                
                {/* Call to Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<Work />}
                    onClick={() => scrollToSection('projects')}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      color: '#1a1a1a',
                      fontWeight: '600',
                      px: 4,
                      py: 1.5,
                      borderRadius: '25px',
                      textTransform: 'none',
                      fontSize: '1rem',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        backgroundColor: 'white',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(255, 255, 255, 0.3)'
                      }
                    }}
                  >
                    View Projects
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Person />}
                    component={RouterLink}
                    to="/cv"
                    sx={{
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      color: 'white',
                      fontWeight: '500',
                      px: 4,
                      py: 1.5,
                      borderRadius: '25px',
                      textTransform: 'none',
                      fontSize: '1rem',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    View CV & Skills
                  </Button>

                </Box>
              </FloatingElement>
            </Grid>
          </Grid>
        </Container>
        <ScrollButton onClick={() => scrollToSection('projects')}>
          <KeyboardArrowDown />
        </ScrollButton>
      </HeroSection>

      {/* Featured Projects Section */}
      <Box id="projects" sx={{ py: 10, backgroundColor: '#1a1a1a' }}>
        <Container maxWidth="xl">
          <Box textAlign="center" sx={{ mb: 8 }}>
            <SectionTitle variant="h2" textAlign="center">
              Featured Projects
            </SectionTitle>
          </Box>
          
          {/* Featured Projects */}
          <Box sx={{ mb: 8 }}>
            <Grid container spacing={4}>
              {portfolioData.projects.filter(p => p.featured).map((project, index) => (
                <Grid item xs={12} lg={4} key={project.id}>
                  <Fade in={true} timeout={800 + index * 300}>
                    <FeaturedProjectCard>
                      <ProjectImage
                        className="project-image"
                        image={getImageUrl(project.image)}
                        title={project.title}
                      />
                      <FeaturedOverlay className="featured-overlay">
                        {/* Header Section */}
                        <Box>
                          <Typography variant="h3" sx={{ 
                            fontWeight: '700', 
                            mb: 2,
                            background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            letterSpacing: '-0.02em'
                          }}>
                            {project.title}
                          </Typography>
                          <Box sx={{ 
                            width: '60px', 
                            height: '2px', 
                            background: 'linear-gradient(90deg, #ffffff 0%, transparent 100%)',
                            mb: 3
                          }} />
                        </Box>

                        {/* Content Section */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <Typography variant="body1" sx={{ 
                            fontSize: '1.1rem',
                            lineHeight: 1.7,
                            mb: 4,
                            opacity: 0.95,
                            fontWeight: '400',
                            textAlign: 'left'
                          }}>
                            {project.longDescription}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 1.5, mb: 4, flexWrap: 'wrap' }}>
                            {project.technologies.map((tech) => (
                              <Chip 
                                key={tech} 
                                label={tech} 
                                size="small"
                                sx={{ 
                                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                  color: 'white',
                                  fontSize: '0.75rem',
                                  fontWeight: '500',
                                  border: '1px solid rgba(255, 255, 255, 0.2)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                                  }
                                }} 
                              />
                            ))}
                          </Box>
                        </Box>

                        {/* Actions Section */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {project.github && (
                            <Button
                              startIcon={<GitHub />}
                              variant="outlined"
                              onClick={() => window.open(project.github, '_blank')}
                              sx={{ 
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                color: 'white',
                                fontWeight: '500',
                                px: 3,
                                py: 1,
                                textTransform: 'none',
                                fontSize: '0.95rem',
                                '&:hover': { 
                                  borderColor: 'rgba(255, 255, 255, 0.6)',
                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                  transform: 'translateY(-1px)'
                                }
                              }}
                            >
                              View Code
                            </Button>
                          )}
                          {project.demo && (
                            <Button
                              startIcon={<Launch />}
                              variant="contained"
                              onClick={() => window.open(project.demo, '_blank')}
                              sx={{ 
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                color: '#1a1a1a',
                                fontWeight: '600',
                                px: 3,
                                py: 1,
                                textTransform: 'none',
                                fontSize: '0.95rem',
                                '&:hover': { 
                                  backgroundColor: 'white',
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.3)'
                                }
                              }}
                            >
                              Live Demo
                            </Button>
                          )}
                        </Box>
                      </FeaturedOverlay>
                      <CardContent sx={{ p: 4 }}>
                        <Typography variant="h4" sx={{ mb: 2, fontWeight: '600', color: '#f0f0f0' }}>
                          {project.title}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6, color: '#c0c0c0' }}>
                          {project.description}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {project.technologies.map((tech) => (
                            <SkillChip key={tech} label={tech} size="medium" />
                          ))}
                        </Box>
                      </CardContent>
                    </FeaturedProjectCard>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Other Projects */}
          {/* <Box>
            <Typography variant="h4" sx={{ mb: 4, fontWeight: '600', textAlign: 'center', color: '#d0d0d0' }}>
              💼 More Projects
            </Typography>
            <Grid container spacing={3}>
              {portfolioData.projects.filter(p => !p.featured).map((project, index) => (
                <Grid item xs={12} md={6} lg={4} key={project.id}>
                  <Slide in={true} direction="up" timeout={600 + index * 200}>
                    <RegularProjectCard>
                      <CardMedia
                        component="img"
                        height="200"
                        image={project.image}
                        alt={project.title}
                      />
                      <ProjectOverlay className="project-overlay">
                        <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                          {project.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                          {project.github && (
                            <Button
                              startIcon={<GitHub />}
                              variant="contained"
                              size="small"
                              onClick={() => window.open(project.github, '_blank')}
                              sx={{ 
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' }
                              }}
                            >
                              Code
                            </Button>
                          )}
                          {project.demo && (
                            <Button
                              startIcon={<Launch />}
                              variant="contained"
                              size="small"
                              onClick={() => window.open(project.demo, '_blank')}
                              sx={{ 
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' }
                              }}
                            >
                              Demo
                            </Button>
                          )}
                        </Box>
                      </ProjectOverlay>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', color: '#f0f0f0' }}>
                          {project.title}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, color: '#c0c0c0' }}>
                          {project.description}
                        </Typography>
                        <Box>
                          {project.technologies.slice(0, 4).map((tech) => (
                            <SkillChip key={tech} label={tech} size="small" />
                          ))}
                        </Box>
                      </CardContent>
                    </RegularProjectCard>
                  </Slide>
                </Grid>
              ))}
            </Grid>
          </Box> */}
        </Container>
      </Box>





      {/* Publications Section */}
      <Box id="publications" sx={{ py: 10, backgroundColor: '#222222', color: 'white' }}>
        <Container maxWidth="lg">
          <SectionTitle variant="h3" textAlign="center" sx={{ mb: 6 }}>
            Publications & Research
          </SectionTitle>
          
          <Grid container spacing={6} justifyContent="center">
            {/* Master's Thesis */}
            <Grid item xs={12} sm={6} md={4}>
              <PublicationCard
                onClick={() => window.open('https://ntnuopen.ntnu.no/ntnu-xmlui/handle/11250/2976466', '_blank')}
              >
                <CardMedia
                  component="img"
                  height="100%"
                  image={getImageUrl("master_thesis.png", 'publications')}
                  alt="Master's Thesis Cover"
                  sx={{ 
                    objectFit: 'cover',
                    height: '100%',
                    filter: 'brightness(0.8)'
                  }}
                />
                <PublicationOverlay className="publication-overlay">
                  <Typography variant="h6" sx={{ fontWeight: '600', mb: 1 }}>
                    Pulmonary Tumor Segmentation Utilizing Mixed-Supervision in a Teacher-Student Framework
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                    Norwegian University of Science and Technology
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Launch sx={{ fontSize: 16 }} />
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      View Publication
                    </Typography>
                  </Box>
                </PublicationOverlay>
                
                {/* Always visible info */}
                <Box sx={{ 
                  position: 'absolute', 
                  bottom: 0, 
                  left: 0, 
                  right: 0, 
                  background: 'rgba(0, 0, 0, 0.7)',
                  p: 2
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: '600', color: 'white' }}>
                    Master's Thesis
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#d0d0d0', fontSize: '0.8rem' }}>
                    NTNU • 2021
                  </Typography>
                </Box>
              </PublicationCard>
            </Grid>
            
            {/* Research Paper */}
            <Grid item xs={12} sm={6} md={4}>
              <PublicationCard
                onClick={() => window.open('https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0266147', '_blank')}
              >
                <CardMedia
                  component="img"
                  height="100%"
                  image={getImageUrl("plos_one.png")}
                  alt="Research Paper Cover"
                  sx={{ 
                    objectFit: 'cover',
                    height: '100%',
                    filter: 'brightness(0.8)'
                  }}
                />
                <PublicationOverlay className="publication-overlay">
                  <Typography variant="h6" sx={{ fontWeight: '600', mb: 1 }}>
                    Teacher-student approach for lung tumor segmentation from mixed-supervised datasets
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                    PLOS One
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Launch sx={{ fontSize: 16 }} />
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      View Publication
                    </Typography>
                  </Box>
                </PublicationOverlay>
                
                {/* Always visible info */}
                <Box sx={{ 
                  position: 'absolute', 
                  bottom: 0, 
                  left: 0, 
                  right: 0, 
                  background: 'rgba(0, 0, 0, 0.7)',
                  p: 2
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: '600', color: 'white' }}>
                    Research Paper
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#d0d0d0', fontSize: '0.8rem' }}>
                    PLOS One • 2022
                  </Typography>
                </Box>
              </PublicationCard>
            </Grid>
            
            {/* Add more publications by copying the Grid item above */}
            
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default PortfolioPage;